import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';

interface ContactRecord {
  id: number;
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
}

@Component({
  selector: 'app-my-contacts-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './app-my-contacts-page.component.html',
  styleUrls: ['./app-my-contacts-page.component.scss']
})
export class MyContactsPageComponent {
  private readonly formBuilder = inject(FormBuilder);
  private nextId = 4;

  readonly searchControl = this.formBuilder.control('');
  readonly activeModal = signal<'form' | 'delete' | null>(null);
  readonly editingContact = signal<ContactRecord | null>(null);
  readonly contacts = signal<ContactRecord[]>([
    {
      id: 1,
      fullName: 'Lerato Molefe',
      phoneNumber: '+27 82 111 2211',
      email: 'lerato.molefe@example.com',
      address: '12 Bosman Street, Pretoria, Gauteng'
    },
    {
      id: 2,
      fullName: 'Aiden Naidoo',
      phoneNumber: '+27 73 444 8899',
      email: 'aiden.naidoo@example.com',
      address: '44 Marine Drive, Durban North, KwaZulu-Natal'
    },
    {
      id: 3,
      fullName: 'Nomsa Moyo',
      phoneNumber: '+27 61 555 0180',
      email: 'nomsa.moyo@example.com',
      address: '87 4th Avenue, Randburg, Gauteng'
    }
  ]);

  readonly contactForm = this.formBuilder.group({
    fullName: ['', Validators.required],
    phoneNumber: ['', Validators.required],
    email: ['', [Validators.required, Validators.email]],
    address: ['', Validators.required]
  });

  readonly filteredContacts = computed(() => {
    const query = (this.searchControl.value ?? '').trim().toLowerCase();
    if (!query) {
      return this.contacts();
    }

    return this.contacts().filter((contact) =>
      [contact.fullName, contact.phoneNumber, contact.email, contact.address]
        .join(' ')
        .toLowerCase()
        .includes(query)
    );
  });

  openAddModal(): void {
    this.editingContact.set(null);
    this.contactForm.reset({ fullName: '', phoneNumber: '', email: '', address: '' });
    this.activeModal.set('form');
  }

  openEditModal(contact: ContactRecord): void {
    this.editingContact.set(contact);
    this.contactForm.reset({
      fullName: contact.fullName,
      phoneNumber: contact.phoneNumber,
      email: contact.email,
      address: contact.address
    });
    this.activeModal.set('form');
  }

  openDeleteModal(contact: ContactRecord): void {
    this.editingContact.set(contact);
    this.activeModal.set('delete');
  }

  saveContact(): void {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      return;
    }

    const value = this.contactForm.getRawValue() as Omit<ContactRecord, 'id'>;
    const existing = this.editingContact();

    if (existing) {
      this.contacts.update((contacts) =>
        contacts.map((contact) => (contact.id === existing.id ? { ...contact, ...value } : contact))
      );
    } else {
      this.contacts.update((contacts) => [...contacts, { id: this.nextId++, ...value }]);
    }

    this.closeModal();
  }

  deleteContact(): void {
    const existing = this.editingContact();
    if (!existing) {
      return;
    }

    this.contacts.update((contacts) => contacts.filter((contact) => contact.id !== existing.id));
    this.closeModal();
  }

  closeModal(): void {
    this.activeModal.set(null);
    this.editingContact.set(null);
  }
}